import React, { useCallback, useEffect, useMemo, useState, useRef, memo, Component } from 'react';
/**
 * PracticeSession — Core practice/assessment session orchestrator.
 *
 * DDD co-locatie: moved from components/practice/ in s69.
 * Optimized: extracted 7 full-page states + exit modal → practiceSessionStates.tsx (~200r).
 * Fluency feedback states extracted → FluencyFeedbackStates.tsx.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { type Question, type ModuleId, getQuestionTypeConfig, getLearningGoal, getAllQuestions, getAllowedTiersForGroup, MODULES, getModuleHexColor, getModuleTint, getFamilyDefinition } from '../../content';
import type { FamilyMasteryState } from '../../mastery';
import { getAdaptiveSessionV11, getFamilyMasteryScore } from '../../mastery';
import { Button } from '../../components/ui/Button';
import { QuestionRendererFactory } from './renderers/QuestionRendererFactory';
import { FeedbackDisplay } from './feedback/FeedbackDisplay';
import { PracticeHeaderMinimal } from './PracticeHeaderMinimal';
import { QuestionCardClean } from './QuestionCardClean';
import { ComboIndicator } from '../../components/shared/ComboIndicator';
import { CheatSheetDrawer } from '../../support';
import { ScratchpadDrawer } from './ScratchpadDrawer';
import { SessionSummary } from './SessionSummary';
import { SessionLoadingState, SessionErrorState, SessionEmptyNoContent, SessionEmptyAllMastered, SessionEmptyFallback, SessionSafetyError, PracticeExitModal } from './practiceSessionStates';
import { FluencyCorrectState, FluencyIncorrectState } from './FluencyFeedbackStates';
import { getMotivationalMessage, getStreakMessage, getLevelTitle } from '../../utils/motivationalMessages';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
import { SESSION_CONFIG, calculateXP } from '../../config/constants';
import { ArrowRight, NotebookPen, HelpCircle } from 'lucide-react';
import { validateSessionAvailability } from '../logic/sessionValidation';
import { debug, warn, error as logError } from '../../utils/logger';
import { soundManager } from '../../utils/soundManager';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { DjayAvatar, useDjaySelection } from '../../mascot';
import { createMasteryQueries } from '../../accounts/hooks/useLearnerActions';
import { isFastResponse } from '../logic/questionTimeBaselines';
import { getTodayChallenge } from '../../utils/dailyChallenge';
import { filterSession, evaluateQuestion } from '../logic/pedagogicalGuard';
import { V11_CONFIG } from '../../mastery';
// ── Types ────────────────────────────────────────────────────
interface PracticeSessionProps {
  moduleId: ModuleId | null;
  questions: Question[];
  onComplete: (summary?: {
    answeredCount: number;
  }) => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number, payload?: string, errorType?: string, delta?: number, timeMs?: number, helpUsed?: boolean, skipped?: boolean) => void;
  cheatSheetViewed: boolean;
  onCheatSheetView: () => void;
  isCrossModule?: boolean;
  isFluency?: boolean;
  labMode?: string;
  pendingLevelUp?: number | null;
  xp?: number;
  onClearPendingLevelUp?: () => void;
}
type Phase = 'question' | 'feedback' | 'summary' | 'fluency-correct' | 'fluency-incorrect';
interface SessionAnswer {
  questionId: string;
  isCorrect: boolean;
  familyId?: string;
  questionType: string;
  errorType?: string;
  timeMs?: number;
}
// ── Helpers ──────────────────────────────────────────────────
function isInteractiveQuestion(question?: Question): boolean {
  if (!question) return false;
  return getQuestionTypeConfig(question.questionType || 'multiple_choice').isInteractive;
}
function getResetState() {
  return {
    currentQuestionIndex: 0,
    phase: 'question' as Phase,
    selectedOption: null as number | null,
    userAnswerIndex: null as number | null,
    userTypedAnswer: '',
    actualIsCorrect: null as boolean | null,
    interactiveReady: false,
    shouldValidateInteractive: false,
    currentStreak: 0,
    totalXpGained: 0
  };
}
// ── Component ────────────────────────────────────────────────
export function PracticeSession({
  moduleId,
  questions,
  onComplete,
  onExit,
  onAnswer,
  cheatSheetViewed,
  onCheatSheetView,
  isCrossModule = false,
  isFluency = false,
  labMode,
  pendingLevelUp = null,
  xp = 0,
  onClearPendingLevelUp
}: PracticeSessionProps) {
  const {
    state,
    selectedGroup,
    groupVersion,
    isInitialized,
    manualPeriod,
    updateDailyChallengeProgress
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const {
    shouldAnimate
  } = useMotionBudget();
  // Mode flags
  const isChallenge = labMode === 'challenge';
  const showFeedback = true;
  const showStreak = true;
  const showCheatSheet = !isCrossModule;
  // Session state
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('question');
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const lastGroupVersionRef = useRef(groupVersion);
  // Answer state
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [actualIsCorrect, setActualIsCorrect] = useState<boolean | null>(null);
  const [userTypedAnswer, setUserTypedAnswer] = useState<string>('');
  const [userAnswerIndex, setUserAnswerIndex] = useState<number | null>(null);
  const [interactiveReady, setInteractiveReady] = useState(false);
  const [shouldValidateInteractive, setShouldValidateInteractive] = useState(false);
  // UI state
  const [isCheatSheetOpen, setIsCheatSheetOpen] = useState(false);
  const [isScratchpadOpen, setIsScratchpadOpen] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // ── Skip question feature ────────────────────────────────
  // "Ik weet het echt niet" is a graceful escape hatch — but heavily gated
  // so it stays meaningful instead of becoming a tap-through shortcut.
  // Three conditions must all be true before the link appears:
  //   1. The learner has been on this question for ≥20s (real engagement)
  //   2. The learner has opened the cheatsheet at least once this session
  //      (we want them to try the scaffolding first)
  //   3. Fewer than 2 skips used in this session
  // On confirm: no XP, no daily-goal credit, mastery dampened (skip path in
  // updateFamilyMastery), session-local streak resets, and the family's
  // remaining questions in the buffer are swapped out (P-13-style).
  const MAX_SKIPS_PER_SESSION = 2;
  const SKIP_TIME_THRESHOLD_MS = 20000;
  const SKIP_FLASH_DURATION_MS = 1800;
  const [skipCount, setSkipCount] = useState(0);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [cheatSheetEverOpened, setCheatSheetEverOpened] = useState(false);
  const [canSkipTimePassed, setCanSkipTimePassed] = useState(false);
  const [skipFlashVisible, setSkipFlashVisible] = useState(false);
  const skipTimeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const skipFlashTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Help-used tracking: did the learner open the cheatsheet during the current
  // question? Resets on every new question. When true at submit-time, the
  // mastery update halves the K-factor for a correct answer (no UI penalty,
  // no XP penalty — purely a signal-quality adjustment). See
  // CHEATSHEET_HINT_SYSTEM_REDESIGN_2026.md §13.
  const helpUsedForCurrentQuestionRef = useRef(false);
  // Stats
  const [currentStreak, setCurrentStreak] = useState(0);
  const [speedStreak, setSpeedStreak] = useState(0);
  const [motivationalText, setMotivationalText] = useState('');
  const [xpGained, setXpGained] = useState(0);
  const [totalXpGained, setTotalXpGained] = useState(0);
  // Celebration state
  const [streakMilestone, setStreakMilestone] = useState<{
    count: number;
    message: string;
  } | null>(null);
  const [dailyGoalReached, setDailyGoalReached] = useState(false);
  // Session tracking for summary
  const [sessionAnswers, setSessionAnswers] = useState<SessionAnswer[]>([]);
  const [preFamilyMastery, setPreFamilyMastery] = useState<Record<string, FamilyMasteryState>>({});
  const questionStartTimeRef = useRef<number>(Date.now());
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const answerProcessedRef = useRef(false);
  // Fluency elapsed timer
  const [elapsedMs, setElapsedMs] = useState(0);
  // Challenge adaptivity
  const challengeConsecutiveErrorsRef = useRef(0);
  const [challengeTierDowngraded, setChallengeTierDowngraded] = useState(false);
  const challengeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Cheat sheet nudge
  const [cheatSheetNudge, setCheatSheetNudge] = useState(false);
  const nudgeFiredRef = useRef(false);
  const consecutiveErrorsRef = useRef(0);
  const submitAnswerRef = useRef<() => void>(() => {});
  // ── Idle Nudge (§16.H) ───────────────────────────────────
  // v2 (mei 2026): de oude implementatie triggerde na een vaste 15s en
  // bleef vervolgens permanent staan — vooral bij lange vragen kwam de
  // bubbel al tijdens het lezen, en hij ging nooit meer weg. Nieuw gedrag:
  //   • Basistimeout 45s, +1s per 20 leestekens van de vraag, cap 90s
  //   • Bubbel verschijnt max. 1× per vraag (geen herhaling als de leerling
  //     hem dismisst en weer stil wordt)
  //   • Globale cooldown 60s tussen twee idle-bubbels in dezelfde sessie
  //   • Geluid alleen bij de ALLEREERSTE idle van de sessie — daarna stil
  //   • Bubbel auto-dismist na 8s via DjayBubble's autoDismissMs
  const IDLE_BASE_MS = 45000;
  const IDLE_PER_20_CHARS_MS = 1000;
  const IDLE_MAX_MS = 90000;
  const IDLE_GLOBAL_COOLDOWN_MS = 60000;
  const [isIdle, setIsIdle] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const idleShownForCurrentQuestionRef = useRef(false);
  const lastIdleShownAtRef = useRef(0);
  const idleSoundPlayedRef = useRef(false);
  // Compute per-question idle timeout based on question prompt length.
  // We read from sessionQuestions directly (not `currentQuestion`) because
  // this hook is declared above the `currentQuestion` const further down —
  // referencing it here would hit the TDZ on initial mount.
  const idleTimeoutMs = useMemo(() => {
    const q = sessionQuestions[currentQuestionIndex];
    const textLen = (q?.question || '').length;
    const bonus = Math.floor(textLen / 20) * IDLE_PER_20_CHARS_MS;
    return Math.min(IDLE_MAX_MS, IDLE_BASE_MS + bonus);
  }, [sessionQuestions, currentQuestionIndex]);
  const dismissIdle = useCallback(() => {
    setIsIdle(false);
  }, []);
  const resetIdleTimer = useCallback(() => {
    setIsIdle(false);
    clearTimeout(idleTimerRef.current);
    // Only run idle timer during active question phase, and only if the
    // bubble hasn't already been shown for this question.
    if (phase !== 'question' || isCheatSheetOpen || idleShownForCurrentQuestionRef.current) {
      return;
    }
    idleTimerRef.current = setTimeout(() => {
      // Respect global cooldown across questions
      const now = Date.now();
      if (now - lastIdleShownAtRef.current < IDLE_GLOBAL_COOLDOWN_MS) {
        return;
      }
      idleShownForCurrentQuestionRef.current = true;
      lastIdleShownAtRef.current = now;
      setIsIdle(true);
      // Sound only on the first idle of the session
      if (!idleSoundPlayedRef.current) {
        soundManager.playDjaySpeak();
        idleSoundPlayedRef.current = true;
      }
    }, idleTimeoutMs);
  }, [phase, isCheatSheetOpen, idleTimeoutMs]);
  // Reset on phase change or cheatsheet toggle
  useEffect(() => {
    resetIdleTimer();
    return () => clearTimeout(idleTimerRef.current);
  }, [resetIdleTimer]);
  // Reset per-question idle state whenever the question changes
  useEffect(() => {
    idleShownForCurrentQuestionRef.current = false;
    setIsIdle(false);
    // Auto-close scratchpad on question change so the new question starts
    // on a clean canvas. The drawer itself resets via resetKey, but closing
    // also restores screen real estate for the next question.
    setIsScratchpadOpen(false);
  }, [currentQuestionIndex]);
  // ── Per-question 20s timer for skip eligibility ──────────
  // Resets on every question change. We track this as state (not a ref)
  // because the "Ik weet het echt niet" link's visibility depends on it
  // and must trigger a re-render when the threshold passes.
  useEffect(() => {
    setCanSkipTimePassed(false);
    clearTimeout(skipTimeTimerRef.current);
    if (phase !== 'question') return;
    skipTimeTimerRef.current = setTimeout(() => {
      setCanSkipTimePassed(true);
    }, SKIP_TIME_THRESHOLD_MS);
    return () => clearTimeout(skipTimeTimerRef.current);
  }, [currentQuestionIndex, phase]);
  // ── P-12: Frustration detection (per-family consecutive errors) ───
  const familyErrorStreaksRef = useRef<Map<string, number>>(new Map());
  // ── P-13: Mid-session difficulty adjustment ───────────────
  const lastDifficultyAdjustIndexRef = useRef<number>(-999);
  // ── Reset helper ─────────────────────────────────────────
  const resetSession = () => {
    const r = getResetState();
    setSessionQuestions([]);
    setSessionInitialized(false);
    setCurrentQuestionIndex(r.currentQuestionIndex);
    setPhase(r.phase);
    setSelectedOption(r.selectedOption);
    setUserAnswerIndex(r.userAnswerIndex);
    setUserTypedAnswer(r.userTypedAnswer);
    setActualIsCorrect(r.actualIsCorrect);
    setInteractiveReady(r.interactiveReady);
    setShouldValidateInteractive(r.shouldValidateInteractive);
    setCurrentStreak(r.currentStreak);
    setSpeedStreak(0);
    setSessionAnswers([]);
    setTotalXpGained(r.totalXpGained);
    setCheatSheetNudge(false);
    nudgeFiredRef.current = false;
    consecutiveErrorsRef.current = 0;
    challengeConsecutiveErrorsRef.current = 0;
    familyErrorStreaksRef.current.clear();
    setChallengeTierDowngraded(false);
    // Reset skip-feature state — skip count and the cheatsheet-opened flag
    // are session-scoped, not question-scoped.
    setSkipCount(0);
    setCheatSheetEverOpened(false);
    setCanSkipTimePassed(false);
    setShowSkipConfirm(false);
    setSkipFlashVisible(false);
    clearTimeout(autoAdvanceTimerRef.current);
    clearTimeout(challengeTimerRef.current);
    clearTimeout(idleTimerRef.current);
    clearTimeout(skipTimeTimerRef.current);
    clearTimeout(skipFlashTimerRef.current);
    answerProcessedRef.current = false;
  };
  // ── Cleanup timers on unmount ────────────────────────────
  useEffect(() => {
    return () => {
      clearTimeout(autoAdvanceTimerRef.current);
      clearTimeout(challengeTimerRef.current);
      clearTimeout(idleTimerRef.current);
      clearTimeout(skipTimeTimerRef.current);
      clearTimeout(skipFlashTimerRef.current);
    };
  }, []);
  // ── Exit handling ────────────────────────────────────────
  // F-QA-1 / F-UX-NEW-1: Only prompt for confirmation when the learner is
  // mid-question (i.e. an in-flight answer would be lost). During feedback /
  // fluency-correct / fluency-incorrect / summary phases the previous answer
  // is already committed, so a confirmation modal would feel misleading
  // ("am I about to lose what I just answered?") — better to exit directly.
  const requestExit = useCallback(() => {
    if (phase === 'question' && sessionAnswers.length > 0) {
      setShowExitConfirm(true);
    } else {
      onExit();
    }
  }, [phase, sessionAnswers.length, onExit]);
  // ── Escape key ───────────────────────────────────────────
  const handleEscapePress = useCallback(() => {
    if (showExitConfirm) {
      setShowExitConfirm(false);
      return;
    }
    requestExit();
  }, [requestExit, showExitConfirm]);
  useEscapeKey(handleEscapePress, !isCheatSheetOpen);
  // ── Session initialization (adaptive engine) ─────────────
  useEffect(() => {
    if (!isInitialized) return;
    if (lastGroupVersionRef.current !== groupVersion) {
      lastGroupVersionRef.current = groupVersion;
      resetSession();
      debug('Session fully reset due to group change');
    }
    if (!sessionInitialized) {
      setInitError(null);
      if (state.familyMastery) {
        try {
          // PRACTICE_DOMAIN_RC_AUDIT cross-cut: structuredClone is native and
          // preserves Date/Map/Set/undefined (JSON round-trip dropped them).
          setPreFamilyMastery(structuredClone(state.familyMastery));
        } catch (err) {
          logError('Failed to clone familyMastery:', err);
          setPreFamilyMastery({});
        }
      }
      if (isCrossModule) {
        setSessionQuestions(questions);
        setSessionInitialized(true);
      } else if (moduleId) {
        try {
          const familyMastery = state.familyMastery ?? {};
          const questionStates = state.questionStates ?? {};
          const recentFamilies = Object.values(familyMastery).filter(fm => fm && (fm.lastSeen ?? 0) > 0).sort((a, b) => (b.lastSeen ?? 0) - (a.lastSeen ?? 0)).slice(0, 5).map(fm => fm.familyId).filter(id => id && id.length > 0);
          const recentQuestions = Object.entries(questionStates).filter(([_, qs]) => qs && (qs.lastAttemptDate ?? 0) > 0).sort((a, b) => (b[1]?.lastAttemptDate ?? 0) - (a[1]?.lastAttemptDate ?? 0)).slice(0, 10).map(([qId]) => qId.replace(/_g\d+$/, '')).filter(qId => qId && qId.length > 0);
          debug(`v11.2: Generating session for ${moduleId} | Group ${selectedGroup} | Families tracked: ${Object.keys(familyMastery).length}`);
          const queries = createMasteryQueries(state, selectedGroup, manualPeriod);
          const startingTier = queries.getStartingLevel(moduleId, selectedGroup);
          const adaptiveSession = getAdaptiveSessionV11(moduleId, familyMastery, selectedGroup, questionStates, SESSION_CONFIG.QUESTIONS_PER_SESSION, recentFamilies, recentQuestions, undefined, startingTier, undefined, state.inferredMastery);
          if (adaptiveSession.length === 0) {
            warn('Empty session generated - all content mastered or unavailable');
            setInitError('empty_session');
          }
          // ── Pedagogical Guard (PEDAGOGICAL_CONTRACT_SPEC §5) ──
          // Filter session through pedagogical contract checks.
          // Fatal violations are replaced from the module's question pool;
          // soft violations pass through but are logged for governance.
          const allModuleQuestions = getAllQuestions().filter(q => q.module === moduleId);
          const {
            questions: guardedQuestions,
            stats: guardStats
          } = filterSession(adaptiveSession, SESSION_CONFIG.QUESTIONS_PER_SESSION, allModuleQuestions);
          if (guardStats.fatalRejected > 0 || guardStats.softFailed > 0) {
            debug(`[PedagogicalGuard] Session filtered: ${guardStats.passed} passed, ${guardStats.softFailed} soft, ${guardStats.fatalRejected} fatal (${guardStats.replaced} replaced)`);
          }
          setSessionQuestions(guardedQuestions);
          setSessionInitialized(true);
          debug(`Session: ${guardedQuestions.length} questions for ${moduleId} (group ${selectedGroup}, engine: v11.2, guard: ${guardStats.fatalRejected}F/${guardStats.softFailed}S)`);
        } catch (error) {
          logError('Failed to generate session:', error);
          setInitError('generation_failed');
          setSessionQuestions([]);
          setSessionInitialized(true);
        }
      }
    }
  }, [moduleId, isCrossModule, questions, sessionInitialized, selectedGroup, groupVersion, isInitialized, state.familyMastery, state.questionStates]);
  const currentQuestion = sessionQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === sessionQuestions.length - 1;
  // F-PE-2/F-PE-NEW-1/F-ARCH-NEW-1: memoised candidate pool.
  // The P-12 frustration-detection path and the P-13 difficulty-adjust path
  // both used to call `getAllQuestions().filter(...)` on every wrong answer —
  // two full O(n) registry traversals (plus a guard evaluation per question)
  // on the exact keystroke where the learner is waiting for feedback.
  // We pre-compute the module-scoped, group-tier-allowed, guard-approved pool
  // ONCE per (poolModuleId, selectedGroup) tuple. Both hot paths then refine
  // it cheaply via in-memory filters on `usedIds` / `familyId`.
  //
  // Note: in cross-module / mode sessions `moduleId` is null. We fall back to
  // the current question's module — this thrashes the cache when questions
  // cross modules (rare in practice) but is never *worse* than the previous
  // unconditional full-scan behaviour.
  const poolModuleId = moduleId ?? currentQuestion?.module ?? null;
  const moduleEligiblePool = useMemo(() => {
    if (!poolModuleId) return [] as Question[];
    const allowedTiers = getAllowedTiersForGroup(selectedGroup);
    return getAllQuestions().filter(q => q.module === poolModuleId && allowedTiers.has(q.tier) && evaluateQuestion(q as Question).ok);
  }, [poolModuleId, selectedGroup]);
  // Reset answer state when question changes
  useEffect(() => {
    if (!currentQuestion) return;
    setSelectedOption(null);
    setUserAnswerIndex(null);
    setUserTypedAnswer('');
    setInteractiveReady(false);
    setShouldValidateInteractive(false);
    questionStartTimeRef.current = Date.now();
    answerProcessedRef.current = false;
    // Fresh question → fresh help-tracking. Help is per-question, not per-session.
    helpUsedForCurrentQuestionRef.current = false;
  }, [currentQuestion]);
  // Fluency elapsed timer (4 Hz — sufficient for mm:ss display; was 10 Hz which caused
  // ~600 re-renders per 60-second sprint per F-PE-1).
  useEffect(() => {
    if (!isFluency || phase !== 'question') {
      setElapsedMs(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - questionStartTimeRef.current);
    }, 250);
    return () => clearInterval(interval);
  }, [isFluency, phase, currentQuestionIndex]);
  // Enter key handler.
  // F-QA-4 mitigation: ignore Enter when focus is inside a text input,
  // textarea or contentEditable element so that interactive renderers
  // (e.g. fill-in, money-assembler) can use Enter for their own intent
  // (commit a digit, confirm a coin pick) without racing the session-level
  // submit. The session-level submit still fires when focus is on a
  // button / option chip / outside the form.
  useEffect(() => {
    if (!currentQuestion) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase !== 'question' || isCheatSheetOpen) return;
      if (e.key !== 'Enter') return;
      const active = document.activeElement as HTMLElement | null;
      if (active) {
        const tag = active.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || active.isContentEditable === true) {
          return;
        }
      }
      const interactive = isInteractiveQuestion(currentQuestion);
      if (interactive && interactiveReady) submitAnswerRef.current();else if (!interactive && userAnswerIndex !== null) submitAnswerRef.current();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, isCheatSheetOpen, userAnswerIndex, interactiveReady, currentQuestion]);
  // ── Early returns (full-page states) ─────────────────────
  if (!isInitialized || !sessionInitialized) return <SessionLoadingState data-id="element-10017" />;
  if (initError === 'generation_failed') return <SessionErrorState onExit={onExit} data-id="element-10018" />;
  if (sessionInitialized && sessionQuestions.length === 0) {
    const validationReport = moduleId ? validateSessionAvailability(moduleId, selectedGroup) : null;
    const hasContent = validationReport?.isValid ?? false;
    const hasAnyProgress = state.familyMastery && Object.keys(state.familyMastery).some(fId => state.familyMastery![fId].attempts > 0);
    if (!hasContent) return <SessionEmptyNoContent onExit={onExit} data-id="element-10019" />;
    if (hasAnyProgress) return <SessionEmptyAllMastered onExit={onExit} data-id="element-10020" />;
    return <SessionEmptyFallback onExit={onExit} data-id="element-10021" />;
  }
  if (!currentQuestion && sessionQuestions.length > 0) {
    logError('Current question is undefined but session has questions. Index:', currentQuestionIndex);
    return <SessionSafetyError onExit={onExit} data-id="element-10022" />;
  }
  // ── Answer processing ────────────────────────────────────
  const processAnswer = (isCorrect: boolean, distractorIndex: number, errorType?: string, delta?: number, payload?: string) => {
    if (answerProcessedRef.current) return;
    answerProcessedRef.current = true;
    const timeMs = Date.now() - questionStartTimeRef.current;
    setStreakMilestone(null);
    setDailyGoalReached(false);
    // Daily Challenge Progress
    const todayChallenge = getTodayChallenge(state, MODULES, selectedGroup);
    let countsTowardsChallenge = false;
    if (todayChallenge.type === 'snelheid') {
      countsTowardsChallenge = true; // Any question counts
    } else if (todayChallenge.type === 'mix') {
      // For mix, we just count any question. The true logic of "3 different subjects"
      // is complex to track per-question, so we simplify to just answering questions in general
      // or we could check if this module is different from the last one.
      countsTowardsChallenge = true;
    } else if (todayChallenge.moduleId) {
      // For meester and herhaal, must be correct and in the right module
      countsTowardsChallenge = isCorrect && currentQuestion.module === todayChallenge.moduleId;
    }
    if (countsTowardsChallenge) {
      updateDailyChallengeProgress(1, todayChallenge.type, todayChallenge.targetCount);
    }
    if (isFluency) {
      const questionType = currentQuestion.questionType || 'multiple_choice';
      if (isCorrect && isFastResponse(questionType, timeMs)) {
        setSpeedStreak(prev => prev + 1);
      } else {
        setSpeedStreak(0);
      }
    }
    if (isChallenge) {
      if (!isCorrect) {
        challengeConsecutiveErrorsRef.current++;
        if (challengeConsecutiveErrorsRef.current >= 3 && !challengeTierDowngraded) {
          setChallengeTierDowngraded(true);
          clearTimeout(challengeTimerRef.current);
          challengeTimerRef.current = setTimeout(() => setChallengeTierDowngraded(false), SESSION_CONFIG.CHALLENGE_TIER_DOWNGRADE_MS);
        }
      } else {
        challengeConsecutiveErrorsRef.current = 0;
      }
    }
    if (!isCorrect) {
      consecutiveErrorsRef.current++;
      if (consecutiveErrorsRef.current >= 3 && !nudgeFiredRef.current && showCheatSheet) {
        setCheatSheetNudge(true);
        nudgeFiredRef.current = true;
      }
    } else {
      consecutiveErrorsRef.current = 0;
    }
    // ── P-12: Per-family frustration detection ──────────────
    // Track consecutive errors per family. When a family hits the
    // threshold, swap remaining questions from that family out of
    // the session to prevent frustration spirals.
    //
    // F-FOCUS-SWAP-LEAK (mei 2026): Skip this swap during focus sessions
    // (free practice, Sprint, Challenge, homework). Those sessions have a
    // curated, often single-family question pool that the learner explicitly
    // chose. The replacement pool is module-scoped, so swapping would pull
    // in questions from OTHER families in the same module (e.g. TIJD-12-24
    // leaking into a 'vrij oefenen TIJD-AFSTAND' session) — exactly what
    // the focus-session contract is meant to prevent.
    const frustratedFamilyId = currentQuestion.familyId;
    if (frustratedFamilyId && !isCrossModule) {
      if (!isCorrect) {
        const streak = (familyErrorStreaksRef.current.get(frustratedFamilyId) || 0) + 1;
        familyErrorStreaksRef.current.set(frustratedFamilyId, streak);
        if (streak >= V11_CONFIG.FRUSTRATION_CONSECUTIVE_ERRORS) {
          // Find remaining (future) questions from this family
          const futureIndices: number[] = [];
          for (let fi = currentQuestionIndex + 1; fi < sessionQuestions.length; fi++) {
            if (sessionQuestions[fi].familyId === frustratedFamilyId) {
              futureIndices.push(fi);
            }
          }
          if (futureIndices.length > 0) {
            // Build replacement pool: same module, different family,
            // within allowed tiers, passing pedagogical guard
            // F-PE-2/F-PE-NEW-1: refine the pre-computed moduleEligiblePool
            // instead of re-scanning the full registry + re-running the
            // pedagogical guard for every candidate on every wrong answer.
            const usedIds = new Set(sessionQuestions.map(q => q.id));
            const allModuleQuestions = moduleEligiblePool.filter(q => q.familyId !== frustratedFamilyId && !usedIds.has(q.id));
            // Respect per-family cap: count how many questions each family
            // already has in the session to avoid over-representation
            const familyCounts = new Map<string, number>();
            for (const sq of sessionQuestions) {
              if (sq.familyId) {
                familyCounts.set(sq.familyId, (familyCounts.get(sq.familyId) || 0) + 1);
              }
            }
            const replacementPool = allModuleQuestions.filter(q => {
              const count = familyCounts.get(q.familyId!) || 0;
              return count < V11_CONFIG.MAX_QUESTIONS_PER_FAMILY;
            });
            const updated = [...sessionQuestions];
            let replacedCount = 0;
            for (const idx of futureIndices) {
              if (replacementPool.length > 0) {
                const replacement = replacementPool.shift()!;
                updated[idx] = replacement;
                usedIds.add(replacement.id);
                // Update family count for the replacement
                familyCounts.set(replacement.familyId!, (familyCounts.get(replacement.familyId!) || 0) + 1);
                replacedCount++;
              }
              // If no replacement available, keep the original (better than shortening)
            }
            if (replacedCount > 0) {
              setSessionQuestions(updated);
              debug(`[FrustrationDetection] Family ${frustratedFamilyId}: ${streak} consecutive errors → swapped ${replacedCount}/${futureIndices.length} remaining questions`);
            }
          }
        }
      } else {
        // Reset streak on correct answer for this family
        familyErrorStreaksRef.current.set(frustratedFamilyId, 0);
      }
    }
    // ── P-13: Mid-session difficulty adjustment ──────────────
    // Check running accuracy over the last N answers. If the learner
    // is performing very well, swap upcoming questions for harder ones.
    // If performing poorly, swap for easier ones. This creates a
    // "flow channel" keeping the learner in their zone of proximal
    // development. Complementary to P-12 (family swap on frustration).
    //
    // F-FOCUS-SWAP-LEAK (mei 2026): Same guard as P-12 — focus sessions
    // have a pre-curated question pool (free mode = one family, Sprint =
    // one family, homework = teacher-picked). The module-scoped swap
    // would override that intent and inject unrelated families.
    const diffWindow = V11_CONFIG.DIFFICULTY_ADJUST_WINDOW;
    const updatedAnswers = [...sessionAnswers, {
      questionId: currentQuestion.id,
      isCorrect,
      familyId: currentQuestion.familyId,
      questionType: currentQuestion.questionType || 'multiple_choice'
    }];
    if (!isCrossModule && updatedAnswers.length >= diffWindow && currentQuestionIndex - lastDifficultyAdjustIndexRef.current >= V11_CONFIG.DIFFICULTY_ADJUST_COOLDOWN) {
      const recentAnswers = updatedAnswers.slice(-diffWindow);
      const accuracy = recentAnswers.filter(a => a.isCorrect).length / diffWindow;
      const upThreshold = V11_CONFIG.DIFFICULTY_ADJUST_UP_THRESHOLD;
      const downThreshold = V11_CONFIG.DIFFICULTY_ADJUST_DOWN_THRESHOLD;
      let tierShiftDirection: 'up' | 'down' | null = null;
      if (upThreshold > 0 && accuracy >= upThreshold) {
        tierShiftDirection = 'up';
      } else if (downThreshold > 0 && accuracy <= downThreshold) {
        tierShiftDirection = 'down';
      }
      if (tierShiftDirection) {
        const shift = V11_CONFIG.DIFFICULTY_ADJUST_TIER_SHIFT * (tierShiftDirection === 'up' ? 1 : -1);
        const usedIds = new Set(sessionQuestions.map(q => q.id));
        // F-PE-3/F-PE-NEW-1: refine the pre-computed moduleEligiblePool
        // (same module, allowed tiers, guard-approved) instead of running
        // a second full registry scan on the same answer-submit keystroke.
        const allModuleQuestions = moduleEligiblePool.filter(q => !usedIds.has(q.id));
        const updated = [...sessionQuestions];
        let swappedCount = 0;
        for (let fi = currentQuestionIndex + 1; fi < updated.length; fi++) {
          const orig = updated[fi];
          const targetTier = orig.tier + shift;
          // Find a replacement at the target tier (or nearby)
          const replacementIdx = allModuleQuestions.findIndex(q => q.tier === targetTier && !usedIds.has(q.id));
          if (replacementIdx !== -1) {
            const replacement = allModuleQuestions.splice(replacementIdx, 1)[0];
            updated[fi] = replacement;
            usedIds.add(replacement.id);
            swappedCount++;
          }
        }
        if (swappedCount > 0) {
          setSessionQuestions(updated);
          lastDifficultyAdjustIndexRef.current = currentQuestionIndex;
          debug(`[DifficultyAdjust] accuracy=${(accuracy * 100).toFixed(0)}% over last ${diffWindow} → shifted ${tierShiftDirection} by ${V11_CONFIG.DIFFICULTY_ADJUST_TIER_SHIFT} tier(s), swapped ${swappedCount} upcoming questions`);
        }
      }
    }
    const earned = calculateXP(isCorrect, currentQuestion.tier);
    setXpGained(earned);
    setTotalXpGained(prev => prev + earned);
    const questionsAnsweredToday = (state.dailyStreak?.todayQuestionsAnswered || 0) + 1;
    const dailyGoal = state.dailyStreak?.dailyGoalTarget || SESSION_CONFIG.DAILY_GOAL;
    if (questionsAnsweredToday === dailyGoal) setDailyGoalReached(true);
    if (isCorrect) {
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      const message = getStreakMessage(newStreak);
      if (message) setStreakMilestone({
        count: newStreak,
        message
      });
    } else {
      setCurrentStreak(0);
    }
    setMotivationalText(getMotivationalMessage({
      isCorrect,
      streak: isCorrect ? currentStreak + 1 : 0,
      questionNumber: currentQuestionIndex + 1,
      totalQuestions: sessionQuestions.length
    }));
    setUserAnswerIndex(distractorIndex);
    onAnswer(currentQuestion.id, isCorrect, distractorIndex, payload, errorType, delta, timeMs, helpUsedForCurrentQuestionRef.current);
    setSessionAnswers(prev => [...prev, {
      questionId: currentQuestion.id,
      isCorrect,
      familyId: currentQuestion.familyId,
      questionType: currentQuestion.questionType || 'multiple_choice',
      errorType,
      timeMs
    }]);
    setSelectedOption(null);
    if (isFluency && isCorrect) {
      setPhase('fluency-correct');
      autoAdvanceTimerRef.current = setTimeout(() => nextQuestion(), SESSION_CONFIG.FLUENCY_AUTO_ADVANCE_MS);
    } else if (isFluency && !isCorrect) {
      setPhase('fluency-incorrect');
    } else {
      setPhase('feedback');
    }
  };
  const handleInteractiveComplete = (userAnswer: string) => {
    setUserTypedAnswer(userAnswer);
    const config = getQuestionTypeConfig(currentQuestion.questionType || 'multiple_choice');
    const validation = config.validateAnswer(userAnswer, currentQuestion);
    if (validation.displayAnswer) setUserTypedAnswer(validation.displayAnswer);
    setActualIsCorrect(validation.isCorrect);
    processAnswer(validation.isCorrect, validation.distractorIndex ?? 0, validation.errorType, validation.delta, userAnswer);
  };
  const handleRendererAnswer = (userAnswer: string | number) => {
    if (isInteractiveQuestion(currentQuestion)) handleInteractiveComplete(userAnswer as string);else {
      setUserAnswerIndex(userAnswer as number);
      setSelectedOption(userAnswer as number);
    }
  };
  const submitAnswer = () => {
    if (isInteractiveQuestion(currentQuestion)) {
      setShouldValidateInteractive(true);
      return;
    }
    if (userAnswerIndex === null) return;
    processAnswer(userAnswerIndex === currentQuestion.correctAnswer, userAnswerIndex);
  };
  // Keep ref in sync with latest submitAnswer
  submitAnswerRef.current = submitAnswer;
  const nextQuestion = () => {
    clearTimeout(autoAdvanceTimerRef.current);
    if (onClearPendingLevelUp && pendingLevelUp) onClearPendingLevelUp();
    if (isLastQuestion) {
      setPhase('summary');
    } else {
      // Sound/Haptic audit follow-up (question-transition):
      // Soft 'view-wissel' pop between questions to reinforce flow.
      // Only fires when actually advancing to a next question (not on session end).
      soundManager.playNavigate();
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setActualIsCorrect(null);
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
      setPhase('question');
    }
  };
  // ── Skip flow ────────────────────────────────────────────
  // Eligibility for showing the "Ik weet het echt niet" link.
  const canShowSkip = phase === 'question' && canSkipTimePassed && cheatSheetEverOpened && skipCount < MAX_SKIPS_PER_SESSION && !isFluency && currentQuestion !== undefined;
  // NOTE: intentionally a plain function (not useCallback). This sits AFTER
  // the session-init early returns, so wrapping it in a hook would change the
  // hooks count between the loading render and the normal render → React #310.
  const requestSkip = () => {
    if (!canShowSkip) return;
    setShowSkipConfirm(true);
  };
  const handleSkipConfirm = () => {
    if (answerProcessedRef.current || !currentQuestion) return;
    answerProcessedRef.current = true;
    setShowSkipConfirm(false);
    const timeMs = Date.now() - questionStartTimeRef.current;
    const skippedFamilyId = currentQuestion.familyId;
    // Commit the skip to mastery (dampened path) — no XP, no daily-goal credit.
    onAnswer(currentQuestion.id, false, -1, undefined, 'skipped', undefined, timeMs, helpUsedForCurrentQuestionRef.current, true);
    // Session bookkeeping for the summary screen.
    setSessionAnswers(prev => [...prev, {
      questionId: currentQuestion.id,
      isCorrect: false,
      familyId: currentQuestion.familyId,
      questionType: currentQuestion.questionType || 'multiple_choice',
      errorType: 'skipped',
      timeMs
    }]);
    // Skip resets the in-session combo (same as a wrong answer) — combos
    // reward consistency, and skipping breaks that consistency.
    setCurrentStreak(0);
    setSkipCount(c => c + 1);
    // P-13-style swap: pull remaining same-family questions out of the
    // upcoming buffer so we don't pile more of the same on a learner who
    // just said "I don't know". Replacements come from the same module,
    // different family, within allowed tiers and guard-approved.
    if (skippedFamilyId) {
      const futureIndices: number[] = [];
      for (let fi = currentQuestionIndex + 1; fi < sessionQuestions.length; fi++) {
        if (sessionQuestions[fi].familyId === skippedFamilyId) {
          futureIndices.push(fi);
        }
      }
      if (futureIndices.length > 0) {
        const usedIds = new Set(sessionQuestions.map(q => q.id));
        const familyCounts = new Map<string, number>();
        for (const sq of sessionQuestions) {
          if (sq.familyId) {
            familyCounts.set(sq.familyId, (familyCounts.get(sq.familyId) || 0) + 1);
          }
        }
        const replacementPool = moduleEligiblePool.filter(q => {
          if (q.familyId === skippedFamilyId) return false;
          if (usedIds.has(q.id)) return false;
          const count = familyCounts.get(q.familyId!) || 0;
          return count < V11_CONFIG.MAX_QUESTIONS_PER_FAMILY;
        });
        const updated = [...sessionQuestions];
        let replacedCount = 0;
        for (const idx of futureIndices) {
          if (replacementPool.length > 0) {
            const replacement = replacementPool.shift()!;
            updated[idx] = replacement;
            usedIds.add(replacement.id);
            familyCounts.set(replacement.familyId!, (familyCounts.get(replacement.familyId!) || 0) + 1);
            replacedCount++;
          }
        }
        if (replacedCount > 0) {
          setSessionQuestions(updated);
          debug(`[Skip] Family ${skippedFamilyId} skipped → swapped ${replacedCount}/${futureIndices.length} remaining questions`);
        }
      }
    }
    // Show a brief reassuring Djay flash, then advance. No FeedbackSplash —
    // a skip isn't a teachable moment in the same way a wrong answer is.
    setSkipFlashVisible(true);
    clearTimeout(skipFlashTimerRef.current);
    skipFlashTimerRef.current = setTimeout(() => {
      setSkipFlashVisible(false);
      // Advance after the flash. nextQuestion handles last-question→summary.
      nextQuestion();
    }, SKIP_FLASH_DURATION_MS);
  };
  const handleCheatSheetClose = () => {
    setIsCheatSheetOpen(false);
    if (!cheatSheetViewed) onCheatSheetView();
  };
  // ── Summary phase ────────────────────────────────────────
  if (phase === 'summary') {
    return <SessionSummary moduleId={moduleId ?? '' as ModuleId} sessionQuestions={sessionQuestions} sessionAnswers={sessionAnswers} preFamilyMastery={preFamilyMastery} isFluency={isFluency} labMode={labMode} onExit={() => onComplete({
      answeredCount: sessionQuestions.length
    })} onPracticeAgain={resetSession} xpGained={totalXpGained} data-id="element-10023" />;
  }
  if (!currentQuestion) return <SessionLoadingState data-id="element-10024" />;
  // §3.1 v2 APP_FEEL_2027 — Per-wereld theming, herontworpen:
  // De eerste versie wasste header EN body in dezelfde tint — dat voelde
  // flets en gaf geen diepte. Nu draagt alleen de header een zachte
  // verticale gradient (zie PracticeHeaderMinimal), en blijft de body
  // bewust neutraal (slate-50). Kaarten, antwoorden en feedback rusten
  // zo op een schoon oppervlak; de voortgangsbalk in volledige
  // module-kleur is het enige expliciete identiteitsanker. Fluency mode
  // (amber) blijft z'n eigen visuele wereld.
  const moduleColorClass = moduleId ? MODULES.find(m => m.id === moduleId)?.color || 'text-indigo-500' : 'text-indigo-500';
  const sessionModuleColor = getModuleHexColor(moduleColorClass);
  const worldTint = isFluency ? undefined : getModuleTint(moduleColorClass);
  // Kladpapiertje is alleen zinvol bij rekenkundige modules. We leunen op de
  // `hasNumericContent` flag in de module-definitie — REK/GET/MET/GELD/TIJD
  // staan daar op true. Fluency mode toont 'm bewust niet (snel-en-zeker
  // werkt uit het hoofd, klad zou afleiden).
  const scratchpadEligible = Boolean(moduleId && !isFluency && MODULES.find(m => m.id === moduleId)?.hasNumericContent);
  const familyMasteryPercent = currentQuestion?.familyId ? getFamilyMasteryScore(state.familyMastery ?? {}, currentQuestion.familyId, selectedGroup) : undefined;
  const familyLabel = currentQuestion?.familyId ? getFamilyDefinition(currentQuestion.familyId)?.label : undefined;
  // ── Main render ──────────────────────────────────────────
  return <div className="min-h-screen flex flex-col font-sans bg-slate-50" onMouseMove={resetIdleTimer} onTouchStart={resetIdleTimer} onClick={resetIdleTimer} onKeyDown={resetIdleTimer} data-id="element-10025">
      {showCheatSheet && <CheatSheetDrawer isOpen={isCheatSheetOpen} onClose={handleCheatSheetClose} moduleId={moduleId!} subSkill={currentQuestion.subSkill} familyId={currentQuestion.familyId} data-id="element-10026" />}

      {/* Kladpapiertje — alleen voor numerieke vakken (REK/GET/MET/GELD/TIJD).
                        Puur tooling: geen mastery- of telemetrie-impact. */}
      {scratchpadEligible && <ScratchpadDrawer isOpen={isScratchpadOpen} onClose={() => setIsScratchpadOpen(false)} resetKey={currentQuestion?.id} questionText={currentQuestion?.question} data-id="element-10027" />}

      <PracticeHeaderMinimal currentQuestion={currentQuestionIndex + 1} totalQuestions={sessionQuestions.length} streak={showStreak ? state.streak?.currentTotalStreak || 0 : undefined} speedStreak={speedStreak} isFluency={isFluency} elapsedMs={isFluency && phase === 'question' ? elapsedMs : undefined} moduleColor={sessionModuleColor} worldTint={worldTint} onExit={requestExit} onToggleCheatSheet={showCheatSheet ? () => {
      setIsCheatSheetOpen(true);
      setCheatSheetNudge(false);
      setIsIdle(false);
      // Mark help-used the moment the cheatsheet opens during a
      // live question phase. Once flagged, it stays flagged for
      // this question (even if the learner closes the drawer
      // and submits later) — the help was already consulted.
      if (phase === 'question') {
        helpUsedForCurrentQuestionRef.current = true;
      }
      // Session-scoped: once the cheatsheet has been opened at
      // least once, the learner has earned the right to see the
      // "Ik weet het echt niet" skip option (paired with the
      // 20s and skip-count gates). We want to encourage trying
      // the scaffolding before resorting to skip.
      setCheatSheetEverOpened(true);
    } : undefined} isCheatSheetOpen={isCheatSheetOpen} nudgeActive={cheatSheetNudge} isIdle={isIdle} onIdleDismiss={dismissIdle} learningGoal={currentQuestion ? getLearningGoal(currentQuestion.familyId) : undefined} data-id="element-10028" />

      <div className={`flex-1 max-w-4xl mx-auto w-full px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 flex flex-col min-h-[calc(100vh-64px)] justify-center ${isFluency ? 'bg-amber-50/20' : ''}`} data-id="element-10029">
        {/* Challenge tier-downgrade encouragement banner */}
        <AnimatePresence data-id="element-10030">
          {challengeTierDowngraded && isChallenge && <motion.div key="challenge-downgrade" initial={shouldAnimate ? {
          opacity: 0,
          y: -20,
          scale: 0.95
        } : {
          opacity: 1
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={shouldAnimate ? {
          opacity: 0,
          y: -20,
          scale: 0.95
        } : {
          opacity: 0
        }} transition={shouldAnimate ? {
          type: 'spring',
          stiffness: 350,
          damping: 30
        } : {
          duration: 0
        }} className="mb-4 flex items-center gap-3" data-id="element-10031">
              <motion.div animate={shouldAnimate ? {
            y: [0, -3, 0]
          } : {
            y: 0
          }} transition={shouldAnimate ? {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          } : {
            duration: 0
          }} className="flex-shrink-0" data-id="element-10032">
                <DjayAvatar size="md" emotion="pointing" variant={selectedVariant} animate={shouldAnimate} data-id="element-10033" />
              </motion.div>
              <div className="relative flex-1 max-w-[320px]" data-id="element-10034">
                <div className="bg-violet-50 rounded-2xl shadow-lg border border-violet-200 px-4 py-3" data-id="element-10035">
                  <p className="text-violet-900 font-bold text-sm leading-relaxed" data-id="element-10036">
                    Lastige vragen! 💪
                  </p>
                  <p className="text-violet-700 text-sm mt-1" data-id="element-10037">
                    Even terug naar de basis — dan bouwen we weer op.
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Combo Indicator */}
        {currentStreak >= 2 && <div className="flex justify-center mb-4" data-id="element-10038">
            <ComboIndicator combo={currentStreak} data-id="element-10039" />
          </div>}

        <div className="flex-1 flex flex-col relative" data-id="element-10040">
          <AnimatePresence mode="wait" data-id="element-10041">
            {phase === 'question' && <motion.div key={currentQuestion.id} initial={shouldAnimate ? {
            opacity: 0,
            y: 30,
            scale: 0.97
          } : {
            opacity: 1
          }} animate={{
            opacity: 1,
            x: 0,
            y: 0,
            scale: 1
          }} exit={shouldAnimate ? {
            opacity: 0,
            y: 80,
            rotateZ: -3,
            scale: 0.95
          } : {
            opacity: 0
          }} transition={shouldAnimate ? {
            type: 'spring',
            stiffness: 300,
            damping: 30,
            exit: {
              duration: 0.25,
              ease: 'easeIn'
            }
          } : {
            duration: 0
          }} className="w-full" data-id="element-10042">
                <div data-id="element-10043">
                  <QuestionCardClean question={currentQuestion} isFluency={isFluency} moduleColor={sessionModuleColor} data-id="element-10044">
                    <QuestionRendererFactory question={currentQuestion} onAnswer={handleRendererAnswer} onReadyChange={setInteractiveReady} shouldValidate={shouldValidateInteractive} moduleColor={sessionModuleColor} onSkipQuestion={nextQuestion} data-id="element-10045" />
                  </QuestionCardClean>
                </div>
                <motion.div initial={shouldAnimate ? {
              opacity: 0,
              y: 20
            } : {
              opacity: 1
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={shouldAnimate ? {
              delay: 0.2
            } : {
              duration: 0
            }} className="mt-4 sm:mt-6 md:mt-8 pb-4 flex justify-center" data-id="element-10046">
                  <motion.div className="w-full sm:w-auto rounded-2xl" animate={shouldAnimate && !(isInteractiveQuestion(currentQuestion) ? !interactiveReady : userAnswerIndex === null) ? {
                boxShadow: ['0 0 0 0px rgba(0, 0, 0, 0)', `0 0 0 8px ${sessionModuleColor}26`, '0 0 0 0px rgba(0, 0, 0, 0)']
              } : {}} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-10047">
                    <Button size="xl" disabled={isInteractiveQuestion(currentQuestion) ? !interactiveReady : userAnswerIndex === null} onClick={submitAnswer} className="w-full sm:w-auto sm:min-w-[280px] md:min-w-[360px] shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 rounded-2xl text-fluid-lg" data-id="element-10048">
                      <span className="mr-2 font-black" data-id="element-10049">Controleren</span>
                      <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-10050" />
                    </Button>
                  </motion.div>
                </motion.div>
              </motion.div>}

            {phase === 'fluency-correct' && <FluencyCorrectState currentQuestionIndex={currentQuestionIndex} speedStreak={speedStreak} xpGained={xpGained} selectedVariant={selectedVariant} data-id="element-10051" />}

            {phase === 'fluency-incorrect' && <FluencyIncorrectState currentQuestionIndex={currentQuestionIndex} currentQuestion={currentQuestion} userTypedAnswer={userTypedAnswer} userAnswerIndex={userAnswerIndex} selectedVariant={selectedVariant} onShowFullFeedback={() => setPhase('feedback')} onNext={nextQuestion} data-id="element-10052" />}

            {phase === 'feedback' && showFeedback && <motion.div key={`feedback-${currentQuestionIndex}`} initial={shouldAnimate ? {
            opacity: 0,
            y: 24,
            scale: 0.97
          } : {
            opacity: 1
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={shouldAnimate ? {
            type: 'spring',
            stiffness: 350,
            damping: 30
          } : {
            duration: 0
          }} className="w-full" data-id="element-10053">
                <FeedbackDisplay question={currentQuestion} wasCorrect={isInteractiveQuestion(currentQuestion) ? actualIsCorrect === true : userAnswerIndex !== null && userAnswerIndex === currentQuestion.correctAnswer} userAnswer={isInteractiveQuestion(currentQuestion) ? userTypedAnswer : userAnswerIndex !== null ? currentQuestion.options?.[userAnswerIndex] ?? '' : ''} userAnswerIndex={userAnswerIndex ?? undefined} motivationalText={motivationalText} xpGained={xpGained} onNext={nextQuestion} isLastQuestion={isLastQuestion} mode={isFluency ? 'compact' : 'full'} streak={showStreak ? state.streak?.currentTotalStreak || 0 : undefined} levelUp={pendingLevelUp ? {
              level: pendingLevelUp,
              xp,
              title: getLevelTitle(pendingLevelUp)
            } : null} streakMilestone={streakMilestone} dailyGoalReached={dailyGoalReached} onClearLevelUp={onClearPendingLevelUp} moduleColor={sessionModuleColor} familyMasteryPercent={familyMasteryPercent} familyLabel={familyLabel} data-id="element-10054" />
              </motion.div>}
          </AnimatePresence>
          {/* Skip link */}
          {canShowSkip && <AnimatePresence data-id="element-10055">
              <motion.div key="skip-link" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} className="mt-4 sm:mt-6 md:mt-8 pb-4 flex justify-center" data-id="element-10056">
                <motion.button onClick={requestSkip} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-full font-medium" data-id="element-10057">
                  <HelpCircle className="w-4 h-4" data-id="element-10058" />
                  <span className="text-sm" data-id="element-10059">Ik weet het echt niet</span>
                </motion.button>
              </motion.div>
            </AnimatePresence>}
        </div>

        <PracticeExitModal show={showExitConfirm} answeredCount={sessionAnswers.length} totalQuestions={sessionQuestions.length} onContinue={() => setShowExitConfirm(false)} onStop={() => {
        setShowExitConfirm(false);
        onExit();
      }} data-id="element-10060" />

        {/* Skip confirmation — Djay-styled sheet, not the generic
            ConfirmDialog. Skipping is a healthy learning move, not a
            destructive action, so the framing is warm and reassuring
            ("helemaal oké, ik pak gewoon een andere vraag") rather than
            transactional ("geen punten, geen streak"). */}
        <AnimatePresence data-id="element-10061">
          {showSkipConfirm && <motion.div key="skip-confirm-backdrop" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={() => setShowSkipConfirm(false)} role="dialog" aria-modal="true" aria-labelledby="skip-confirm-title" data-id="element-10062">
              <motion.div initial={shouldAnimate ? {
            opacity: 0,
            scale: 0.92,
            y: 16
          } : {
            opacity: 1
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={shouldAnimate ? {
            opacity: 0,
            scale: 0.95,
            y: 8
          } : {
            opacity: 0
          }} transition={shouldAnimate ? {
            type: 'spring',
            stiffness: 320,
            damping: 26
          } : {
            duration: 0
          }} className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-10063">
                {/* Djay header strip — soft amber, mirrors the warmth of
                    the skip flash so confirm + flash feel like one
                    moment. */}
                <div className="px-6 pt-6 pb-4 bg-gradient-to-b from-amber-50 to-white flex items-start gap-4" data-id="element-10064">
                  <motion.div animate={shouldAnimate ? {
                y: [0, -3, 0]
              } : {
                y: 0
              }} transition={shouldAnimate ? {
                duration: 2.4,
                repeat: Infinity,
                ease: 'easeInOut'
              } : {
                duration: 0
              }} className="flex-shrink-0" data-id="element-10065">
                    <DjayAvatar size="md" emotion="encouraging" variant={selectedVariant} animate={shouldAnimate} data-id="element-10066" />
                  </motion.div>
                  <div className="flex-1 min-w-0 pt-1" data-id="element-10067">
                    <h2 id="skip-confirm-title" className="text-lg font-black text-slate-900 leading-tight" data-id="element-10068">
                      Helemaal oké!
                    </h2>
                    <p className="text-sm text-slate-700 mt-1.5 leading-snug" data-id="element-10069">
                      Zal ik een andere vraag voor je pakken? Daar leer je net
                      zoveel van.
                    </p>
                  </div>
                </div>

                {/* Buttons — primary action is "andere vraag graag"
                    (positive framing). Secondary is "toch nog even
                    proberen" which keeps agency with the learner. */}
                <div className="px-6 pb-6 pt-2 flex flex-col gap-2" data-id="element-10070">
                  <button type="button" onClick={handleSkipConfirm} className="w-full rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 text-sm shadow-md hover:shadow-lg transition-all" data-id="element-10071">
                    Ja, andere vraag graag
                  </button>
                  <button type="button" onClick={() => setShowSkipConfirm(false)} className="w-full rounded-2xl bg-white hover:bg-slate-50 text-slate-700 font-bold py-3 text-sm border border-slate-200 transition-colors" data-id="element-10072">
                    Nog even proberen
                  </button>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Skip flash — brief Djay reassurance after a confirmed skip.
            Auto-dismisses in SKIP_FLASH_DURATION_MS and then advances. */}
        <AnimatePresence data-id="element-10073">
          {skipFlashVisible && <motion.div key="skip-flash" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" data-id="element-10074">
              <motion.div initial={{
            scale: 0.9,
            y: 8
          }} animate={{
            scale: 1,
            y: 0
          }} exit={{
            scale: 0.95
          }} transition={{
            type: 'spring',
            stiffness: 320,
            damping: 24
          }} className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5 flex items-center gap-4" role="status" aria-live="polite" data-id="element-10075">
                <div className="flex-shrink-0" data-id="element-10076">
                  <DjayAvatar size="md" emotion="encouraging" variant={selectedVariant} animate={shouldAnimate} data-id="element-10077" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-10078">
                  <p className="text-base font-bold text-slate-900 leading-snug" data-id="element-10079">
                    Geen zorgen — die slaan we even over.
                  </p>
                  <p className="text-sm text-slate-600 mt-1 leading-snug" data-id="element-10080">
                    We komen er later op terug. 💪
                  </p>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Floating scratchpad trigger — rechtsonder, alleen op rekenkundige
                        modules en alleen tijdens de vraag-fase. Verstopt zich tijdens
                        feedback/summary om geen visuele ruis te geven. */}
      {scratchpadEligible && phase === 'question' && <motion.button type="button" onClick={() => {
      setIsScratchpadOpen(true);
      // Resetting the idle timer here makes sense — opening the
      // scratchpad is active engagement, not idleness.
      resetIdleTimer();
    }} initial={{
      opacity: 0,
      scale: 0.6,
      y: 12
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 22,
      delay: 0.3
    }} whileHover={shouldAnimate ? {
      scale: 1.06,
      y: -2
    } : undefined} whileTap={shouldAnimate ? {
      scale: 0.94
    } : undefined} className="
            fixed z-30
            right-4 sm:right-6
            bottom-[max(1rem,env(safe-area-inset-bottom))]
            sm:bottom-6
            flex items-center gap-2
            px-3.5 py-3 sm:px-4 sm:py-3
            rounded-full
            bg-amber-50 border border-amber-300
            text-amber-800 font-bold
            shadow-lg shadow-amber-900/10
            hover:bg-amber-100 hover:border-amber-400
            transition-colors
          " aria-label="Kladpapiertje openen" title="Kladpapiertje — schrijf of typ je tussenstappen" data-id="element-10081">
          <NotebookPen className="w-5 h-5" strokeWidth={2.4} data-id="element-10082" />
          <span className="hidden sm:inline text-sm" data-id="element-10083">Klad</span>
        </motion.button>}
    </div>;
}